import { Link } from "react-router-dom";
import { Users, MapPin, Calendar, Flame, Heart, Star, Clock, Globe } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { PassionBadge, money, niceDate } from "@/components/ui-bits";

function Img({ src, alt, className }) {
  const PLACEHOLDER = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80";
  return (
    <img
      src={fileUrl(src) || PLACEHOLDER}
      alt={alt}
      loading="lazy"
      className={className}
      onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
    />
  );
}

export function CommunityCard({ c }) {
  return (
    <Link to={`/communities/${c.id}`} className="pa-card pa-card-hover overflow-hidden flex flex-col group" data-testid={`community-card-${c.id}`}>
      <div className="relative h-36 overflow-hidden">
        <Img src={c.cover} alt={c.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-3 left-3"><PassionBadge slug={c.passion} className="backdrop-blur bg-white/80" /></div>
        {c.is_member && <span className="absolute top-3 right-3 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#8FAF9B] text-white">Joined</span>}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-semibold text-[#071126] leading-snug line-clamp-2">{c.name}</h3>
        <p className="text-sm text-[#071126]/55 mt-1 line-clamp-2 flex-1">{c.tagline || c.description}</p>
        <div className="flex items-center gap-3 mt-3 text-xs text-[#55708F] font-medium">
          <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{c.member_count}</span>
          <span className="inline-flex items-center gap-1">{c.location === "Online" ? <Globe className="h-3.5 w-3.5" /> : <MapPin className="h-3.5 w-3.5" />}{c.location || "Global"}</span>
        </div>
      </div>
    </Link>
  );
}

export function EventCard({ e, onClick }) {
  return (
    <Link to={`/events/${e.id}`} className="pa-card pa-card-hover overflow-hidden flex flex-col group" data-testid={`event-card-${e.id}`}>
      <div className="relative h-36 overflow-hidden">
        <Img src={e.cover} alt={e.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <span className="absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/85 backdrop-blur text-[#071126] capitalize">{e.mode}</span>
        <span className="absolute bottom-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full bg-[#FF6B00] text-white">{money(e.price)}</span>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <PassionBadge slug={e.passion} className="self-start mb-2" />
        <h3 className="font-semibold text-[#071126] leading-snug line-clamp-2">{e.title}</h3>
        <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-[#55708F] font-medium">
          <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{niceDate(e.date)}</span>
          {e.start_time && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{e.start_time}</span>}
          <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{e.attendee_count} going</span>
        </div>
      </div>
    </Link>
  );
}

export function ChallengeCard({ c }) {
  return (
    <Link to={`/challenges/${c.id}`} className="pa-card pa-card-hover overflow-hidden flex flex-col group" data-testid={`challenge-card-${c.id}`}>
      <div className="relative h-32 overflow-hidden">
        <Img src={c.cover} alt={c.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#071126]/70 to-transparent" />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#FF6B00] text-white"><Flame className="h-3.5 w-3.5" />{c.duration || "Challenge"}</span>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <PassionBadge slug={c.passion} className="self-start mb-2" />
        <h3 className="font-semibold text-[#071126] leading-snug line-clamp-2">{c.title}</h3>
        <p className="text-sm text-[#071126]/55 mt-1 line-clamp-2 flex-1">{c.description}</p>
        <div className="flex items-center gap-3 mt-3 text-xs text-[#55708F] font-medium">
          <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{c.participant_count} joined</span>
          <span>{c.submission_count} submissions</span>
        </div>
      </div>
    </Link>
  );
}

export function ProjectCard({ p }) {
  return (
    <Link to={`/projects/${p.id}`} className="pa-card pa-card-hover overflow-hidden group flex flex-col" data-testid={`project-card-${p.id}`}>
      <div className="relative aspect-[4/3] overflow-hidden">
        <Img src={p.image} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-[#071126] leading-snug line-clamp-1">{p.title}</h3>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2 min-w-0">
            <img src={fileUrl(p.author?.avatar)} alt="" className="h-6 w-6 rounded-full object-cover" />
            <span className="text-xs text-[#071126]/60 truncate">{p.author?.name}</span>
          </div>
          <span className="inline-flex items-center gap-1 text-xs text-[#55708F]"><Heart className="h-3.5 w-3.5" />{p.like_count ?? 0}</span>
        </div>
      </div>
    </Link>
  );
}

export function ListingCard({ l }) {
  return (
    <Link to={`/marketplace/${l.id}`} className="pa-card pa-card-hover overflow-hidden group flex flex-col" data-testid={`listing-card-${l.id}`}>
      <div className="relative aspect-square overflow-hidden">
        <Img src={l.image} alt={l.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <span className="absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/85 backdrop-blur text-[#071126]">{l.category}</span>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-semibold text-[#071126] leading-snug line-clamp-2 flex-1">{l.title}</h3>
        <div className="flex items-center gap-2 mt-2">
          <img src={fileUrl(l.seller?.avatar)} alt="" className="h-5 w-5 rounded-full object-cover" />
          <span className="text-xs text-[#071126]/55 truncate">{l.seller?.name}</span>
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className="font-bold text-[#FF6B00]">{money(l.price, l.currency)}</span>
          {l.rating > 0 && <span className="inline-flex items-center gap-1 text-xs text-[#55708F]"><Star className="h-3.5 w-3.5 fill-[#FFC94A] text-[#FFC94A]" />{l.rating} ({l.review_count})</span>}
        </div>
      </div>
    </Link>
  );
}

export function PersonCard({ p }) {
  return (
    <Link to={`/u/${p.id}`} className="pa-card pa-card-hover p-4 flex items-center gap-3 group" data-testid={`person-card-${p.id}`}>
      <img src={fileUrl(p.avatar)} alt={p.name} className="h-12 w-12 rounded-full object-cover flex-shrink-0" />
      <div className="min-w-0">
        <h3 className="font-semibold text-[#071126] truncate">{p.name}</h3>
        <p className="text-xs text-[#071126]/55 line-clamp-1">{p.bio || "PassionAte member"}</p>
      </div>
    </Link>
  );
}
